import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FingerprintIcon, KeyRoundIcon, ShieldAlertIcon } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'

import { Logo } from '@/components/logo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

import {
  authStatusKey,
  browserSupportsWebAuthn,
  describeAuthError,
  loginWithPasskey,
  registerPasskey,
} from './api'

export function LoginScreen({ enrollmentOpen }: { enrollmentOpen: boolean }) {
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const supported = browserSupportsWebAuthn()

  useEffect(() => {
    // A sensible default that still tells two devices apart later.
    setLabel(defaultDeviceLabel())
  }, [])

  const onSettled = () => queryClient.invalidateQueries({ queryKey: authStatusKey })

  const enroll = useMutation({
    mutationFn: () => registerPasskey(label.trim() || 'This device'),
    onMutate: () => setMessage(null),
    onError: (error) => setMessage(describeAuthError(error)),
    onSuccess: onSettled,
  })

  const signIn = useMutation({
    mutationFn: () => loginWithPasskey(),
    onMutate: () => setMessage(null),
    onError: (error) => setMessage(describeAuthError(error)),
    onSuccess: onSettled,
  })

  const busy = enroll.isPending || signIn.isPending

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (enrollmentOpen) enroll.mutate()
    else signIn.mutate()
  }

  return (
    <div className="bg-background flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <Logo className="size-9" />
          <div>
            <p className="text-xl font-semibold tracking-tight">Fundus</p>
            <p className="text-muted-foreground text-sm">Component inventory</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{enrollmentOpen ? 'Set up this device' : 'Sign in'}</CardTitle>
            <CardDescription>
              {enrollmentOpen
                ? 'This inventory has no passkey yet. The first one registered claims it.'
                : 'Use the passkey on this device to unlock your inventory.'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {!supported ? (
              <Alert variant="destructive">
                <ShieldAlertIcon />
                <AlertTitle>Passkeys are not available</AlertTitle>
                <AlertDescription>
                  This browser cannot do WebAuthn. Fundus has no password to fall back on — try a
                  current browser over HTTPS.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                {enrollmentOpen && (
                  <>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="passkey-label">Name this device</Label>
                      <Input
                        id="passkey-label"
                        value={label}
                        onChange={(event) => setLabel(event.target.value)}
                        placeholder="Laptop"
                        maxLength={64}
                        autoComplete="off"
                      />
                    </div>
                    <Alert variant="warning">
                      <ShieldAlertIcon />
                      <AlertTitle>Enrollment is open</AlertTitle>
                      <AlertDescription>
                        Anyone who can reach this page right now could claim it. Register your
                        passkey before exposing the instance.
                      </AlertDescription>
                    </Alert>
                  </>
                )}

                {message && (
                  <Alert variant="destructive">
                    <ShieldAlertIcon />
                    <AlertTitle>That did not work</AlertTitle>
                    <AlertDescription>{message}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" disabled={busy} className="w-full">
                  {busy ? (
                    <Spinner label="Waiting for your authenticator" />
                  ) : enrollmentOpen ? (
                    <KeyRoundIcon />
                  ) : (
                    <FingerprintIcon />
                  )}
                  {enrollmentOpen ? 'Create a passkey' : 'Sign in with a passkey'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/** A first guess at a device name, from the user agent. */
function defaultDeviceLabel(): string {
  const ua = navigator.userAgent
  if (/iPhone|Android.*Mobile/.test(ua)) return 'Phone'
  if (/iPad|Tablet/.test(ua)) return 'Tablet'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'PC'
  if (/Linux/.test(ua)) return 'Linux'
  return 'This device'
}
